import { useState, useCallback } from 'react';
import { API_BASE } from '../../../config/constants';

export interface Use2FAResult {
    setup2FA: (userId: number, email: string) => Promise<{ secret: string; qrCode: string } | null>;
    confirm2FA: (userId: number, secret: string, otpCode: string) => Promise<boolean>;
    disable2FA: (userId: number) => Promise<boolean>;
    loading: boolean;
    error: string | null;
}

export function useH2FA(): Use2FAResult {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const setup2FA = useCallback(async (userId: number, email: string) => {
        setLoading(true);
        setError(null);
        try {
            const response = await fetch(`${API_BASE}/api/users/setup-2fa`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ userId, email }),
            });

            const payload = await response.json().catch(() => ({}));
            if (!response.ok || !payload?.success) {
                throw new Error(payload?.message || 'Failed to generate 2FA secret');
            }

            return { secret: payload.secret, qrCode: payload.qrCode };
        } catch (err: any) {
            const errorMsg = err?.message || 'Failed to setup 2FA';
            setError(errorMsg);
            return null;
        } finally {
            setLoading(false);
        }
    }, []);

    const confirm2FA = useCallback(async (userId: number, secret: string, otpCode: string) => {
        setLoading(true);
        setError(null);
        try {
            const response = await fetch(`${API_BASE}/api/users/confirm-2fa-setup`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ userId, secret, otpCode }),
            });

            const payload = await response.json().catch(() => ({}));
            if (!response.ok || !payload?.success) {
                throw new Error(payload?.message || 'Failed to confirm 2FA');
            }

            return true;
        } catch (err: any) {
            const errorMsg = err?.message || 'Failed to confirm 2FA';
            setError(errorMsg);
            return false;
        } finally {
            setLoading(false);
        }
    }, []);

    const disable2FA = useCallback(async (userId: number) => {
        setLoading(true);
        setError(null);
        try {
            const response = await fetch(`${API_BASE}/api/users/disable-2fa`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ userId }),
            });

            const payload = await response.json().catch(() => ({}));
            if (!response.ok || !payload?.success) {
                throw new Error(payload?.message || 'Failed to disable 2FA');
            }

            return true;
        } catch (err: any) {
            const errorMsg = err?.message || 'Failed to disable 2FA';
            setError(errorMsg);
            return false;
        } finally {
            setLoading(false);
        }
    }, []);

    return { setup2FA, confirm2FA, disable2FA, loading, error };
}
